import { Link } from 'react-router-dom';
import { formatCpmCount } from '../../../constants/centralProductMaster.constants';

interface QuickLinksGridProps {
    queueWaiting: number | null;
    heroTotal: number | null;
    uncuratedStates: number | null;
    uncuratedStateName?: string | null;
}

export default function QuickLinksGrid({
    queueWaiting,
    heroTotal,
    uncuratedStates,
    uncuratedStateName,
}: QuickLinksGridProps) {
    // Go-to card always opens the states list (All tab). Per-state deep links
    // live on Excise Readiness rows and attention cards, not this nav card.
    const exciseHref = '/central-product-master/excise-category';

    return (
        <div className="flex flex-col" style={{ gap: 12 }}>
            <div className="flex items-center" style={{ gap: 10 }}>
                <span
                    className="uppercase text-[var(--cpm-text-secondary)]"
                    style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em' }}
                >
                    Go to
                </span>
                <span className="flex-1 h-px bg-[var(--cpm-border-default)]" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3" style={{ gap: 16 }}>
                <Link
                    to="/central-product-master/verification-queue"
                    className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col no-underline !text-inherit hover:border-[var(--cpm-brand-purple)] hover:-translate-y-px transition-all duration-150"
                    style={{ padding: 20, gap: 14 }}
                >
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <span
                            className="flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                            style={{ width: 30, height: 30, borderRadius: 8 }}
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="m3 17 2 2 4-4" />
                                <path d="m3 7 2 2 4-4" />
                                <path d="M13 6h8" />
                                <path d="M13 12h8" />
                                <path d="M13 18h8" />
                            </svg>
                        </span>
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 800,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            Verification Queue
                        </span>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--cpm-text-tertiary)"
                            strokeWidth="2"
                            className="ml-auto"
                        >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </div>
                    <div className="flex items-baseline" style={{ gap: 8 }}>
                        {queueWaiting == null ? (
                            <span
                                className="text-[var(--cpm-shortfall-text-med)]"
                                style={{ fontSize: 12.5, fontWeight: 700 }}
                            >
                                Count unavailable
                            </span>
                        ) : (
                            <>
                                <span
                                    className="tabular text-[var(--cpm-warning-text)]"
                                    style={{
                                        fontSize: 30,
                                        fontWeight: 800,
                                        letterSpacing: '-0.025em',
                                        lineHeight: 1,
                                    }}
                                >
                                    {formatCpmCount(queueWaiting)}
                                </span>
                                <span
                                    className="text-[var(--cpm-warning-text-med)]"
                                    style={{ fontSize: 13, fontWeight: 700 }}
                                >
                                    items waiting on a human
                                </span>
                            </>
                        )}
                    </div>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        Link reviews, first-time promotions, and barcode conflicts.
                    </span>
                </Link>

                <Link
                    to="/central-product-master/master-editor"
                    className="bg-[var(--cpm-surface-card)] border border-[var(--cpm-border-default)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col no-underline !text-inherit hover:border-[var(--cpm-brand-purple)] hover:-translate-y-px transition-all duration-150"
                    style={{ padding: 20, gap: 14 }}
                >
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <span
                            className="flex items-center justify-center bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]"
                            style={{ width: 30, height: 30, borderRadius: 8 }}
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                <path d="M3 9h18" />
                                <path d="M3 15h18" />
                                <path d="M9 3v18" />
                            </svg>
                        </span>
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 800,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            Master Editor
                        </span>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--cpm-text-tertiary)"
                            strokeWidth="2"
                            className="ml-auto"
                        >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </div>
                    <div className="flex items-baseline" style={{ gap: 8 }}>
                        {heroTotal == null ? (
                            <span
                                className="text-[var(--cpm-shortfall-text-med)]"
                                style={{ fontSize: 12.5, fontWeight: 700 }}
                            >
                                Count unavailable
                            </span>
                        ) : (
                            <>
                                <span
                                    className="tabular"
                                    style={{
                                        fontSize: 30,
                                        fontWeight: 800,
                                        letterSpacing: '-0.025em',
                                        lineHeight: 1,
                                    }}
                                >
                                    {formatCpmCount(heroTotal)}
                                </span>
                                <span
                                    className="text-[var(--cpm-text-secondary)]"
                                    style={{ fontSize: 13, fontWeight: 700 }}
                                >
                                    records in the master catalog
                                </span>
                            </>
                        )}
                    </div>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        Search, fix, and merge any record — variants nested under their product.
                    </span>
                </Link>

                <Link
                    to={exciseHref}
                    className={[
                        'bg-[var(--cpm-surface-card)] rounded-[16px] shadow-[var(--cpm-shadow-card)] flex flex-col no-underline !text-inherit hover:-translate-y-px transition-all duration-150',
                        (uncuratedStates ?? 0) > 0
                            ? 'border border-[var(--cpm-warning-border)] hover:border-[var(--cpm-warning-icon)]'
                            : 'border border-[var(--cpm-border-default)] hover:border-[var(--cpm-brand-purple)]',
                    ].join(' ')}
                    style={{ padding: 20, gap: 14 }}
                >
                    <div className="flex items-center" style={{ gap: 10 }}>
                        <span
                            className={[
                                'flex items-center justify-center',
                                (uncuratedStates ?? 0) > 0
                                    ? 'bg-[var(--cpm-warning-bg)] border border-[var(--cpm-warning-border)] text-[var(--cpm-warning-icon)]'
                                    : 'bg-[var(--cpm-purple-tint-13)] text-[var(--cpm-brand-purple)]',
                            ].join(' ')}
                            style={{ width: 30, height: 30, borderRadius: 8 }}
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83z" />
                                <path d="M2 12.18a1 1 0 0 0 .6.91l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 .6-.92" />
                                <path d="M2 17.18a1 1 0 0 0 .6.91l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 .6-.92" />
                            </svg>
                        </span>
                        <span
                            style={{
                                fontSize: 15,
                                fontWeight: 800,
                                letterSpacing: '-0.01em',
                            }}
                        >
                            Excise Category Curation
                        </span>
                        <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="var(--cpm-text-tertiary)"
                            strokeWidth="2"
                            className="ml-auto"
                        >
                            <path d="M5 12h14" />
                            <path d="m12 5 7 7-7 7" />
                        </svg>
                    </div>
                    <div className="flex items-baseline" style={{ gap: 8 }}>
                        {uncuratedStates == null ? (
                            <span
                                className="text-[var(--cpm-shortfall-text-med)]"
                                style={{ fontSize: 12.5, fontWeight: 700 }}
                            >
                                Count unavailable
                            </span>
                        ) : (
                            <>
                                <span
                                    className={[
                                        'tabular',
                                        uncuratedStates > 0 ? 'text-[var(--cpm-warning-text)]' : '',
                                    ].join(' ')}
                                    style={{
                                        fontSize: 30,
                                        fontWeight: 800,
                                        letterSpacing: '-0.025em',
                                        lineHeight: 1,
                                    }}
                                >
                                    {formatCpmCount(uncuratedStates)}
                                </span>
                                <span
                                    className={
                                        uncuratedStates > 0
                                            ? 'text-[var(--cpm-warning-text-med)]'
                                            : 'text-[var(--cpm-text-secondary)]'
                                    }
                                    style={{ fontSize: 13, fontWeight: 700 }}
                                >
                                    {uncuratedStates === 0
                                        ? 'states waiting on curation'
                                        : uncuratedStates === 1 && uncuratedStateName
                                          ? `state (${uncuratedStateName}) is not ready`
                                          : 'states are not ready'}
                                </span>
                            </>
                        )}
                    </div>
                    <span
                        className="text-[var(--cpm-text-secondary)]"
                        style={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.45 }}
                    >
                        Set up each state&apos;s excise category list so the pipeline can create its
                        products&apos; state pricing and excise rows.
                    </span>
                </Link>
            </div>
        </div>
    );
}
